import { useEffect, useState } from "react";
import { Target, Pencil, Check, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function StudyGoals() {
  const [goals, setGoals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");

  const load = () => api.getGoals().then(setGoals).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (g) => { setEditing(g.subject_id); setValue(String(g.target_pct ?? 80)); };
  const cancel = () => { setEditing(null); setValue(""); };

  const save = async (id) => {
    const t = Math.max(0, Math.min(parseInt(value || "0", 10), 100));
    try {
      await api.setGoal(id, t);
      setEditing(null);
      load();
      toast.success("Obiettivo aggiornato!");
    } catch { toast.error("Errore nel salvataggio."); }
  };

  if (goals.length === 0) return null;

  return (
    <div data-testid="study-goals">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
        <Target className="h-3.5 w-3.5" /> Obiettivi di studio
      </div>
      <div className="space-y-2.5">
        {goals.map((g) => {
          const target = g.target_pct;
          const reached = target != null && g.current_pct >= target;
          const remaining = target != null ? Math.max(0, target - g.current_pct) : null;
          return (
            <div key={g.subject_id} className="bg-card border border-border rounded-xl p-4" data-testid={`goal-${g.subject_id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <span className="font-medium text-sm truncate">{g.name}</span>
                </div>
                {editing === g.subject_id ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number" min={0} max={100}
                      data-testid={`goal-input-${g.subject_id}`}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && save(g.subject_id)}
                      className="h-8 w-20 text-sm"
                      autoFocus
                    />
                    <Button data-testid={`goal-save-${g.subject_id}`} size="icon" className="h-8 w-8 rounded-lg" onClick={() => save(g.subject_id)}><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={cancel}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <button data-testid={`goal-edit-${g.subject_id}`} onClick={() => startEdit(g)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
                    {target != null ? `Obiettivo ${target}%` : "Imposta obiettivo"} <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {target != null && (
                <>
                  <Progress value={Math.min(100, target ? (g.current_pct / target) * 100 : 0)} className="h-2" />
                  <div className="flex justify-between text-xs mt-1.5">
                    <span className="text-muted-foreground">Media attuale: {g.current_pct}% {g.attempts_count === 0 && "(nessun quiz)"}</span>
                    {reached ? (
                      <span className="text-emerald-600 font-semibold">Obiettivo raggiunto! 🎉</span>
                    ) : (
                      <span className="text-primary font-semibold">Mancano {remaining}%</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
